import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Slider } from "./ui/slider";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Power, Play, Plus, X, Hand, ArrowLeftRight, ShieldCheck, Plug, CheckCircle2, Circle, MessageSquareText, Flame, FileClock, Ban, Check, MailX, Send, PenLine } from "lucide-react";
import { TemplatesLibrary } from "./TemplatesLibrary";
import { ReferralScheme } from "./ReferralScheme";
import { CompliancePolicy } from "./CompliancePolicy";

export const ControlPanel = ({ open, onOpenChange, settings, business, refresh, paused, onTogglePause }) => {
  const [local, setLocal] = useState(settings);
  const [wordInput, setWordInput] = useState("");
  const [conversations, setConversations] = useState([]);
  const [integrations, setIntegrations] = useState({});
  const [audit, setAudit] = useState([]);
  const [suppression, setSuppression] = useState([]);
  const [suppInput, setSuppInput] = useState("");
  const [unsubInput, setUnsubInput] = useState("");
  const [voicePresets, setVoicePresets] = useState([]);

  useEffect(() => { setLocal(settings); }, [settings]);
  useEffect(() => {
    if (open) {
      api.getConversations().then(setConversations);
      api.integrations().then(setIntegrations);
      api.auditLog().then(setAudit);
      api.getSuppression().then(setSuppression);
      api.getVoicePresets().then((d) => setVoicePresets(d.presets || [])).catch(() => {});
    }
  }, [open]);

  if (!local) return null;

  const commit = async (patch) => { setLocal((l) => ({ ...l, ...patch })); await api.updateSettings(patch); refresh(); };
  const loadPresets = () => api.getVoicePresets().then((d) => setVoicePresets(d.presets || [])).catch(() => {});
  const activatePreset = async (id) => { const s = await api.activateVoicePreset(id); setLocal((l) => ({ ...l, tone: s.tone })); refresh(); toast.success("Voice switched."); };
  const savePreset = async () => {
    const name = window.prompt("Name this voice preset:");
    if (!name) return;
    await api.addVoicePreset(name.trim(), local.tone || "");
    await loadPresets();
    toast.success("Preset saved.");
  };
  const delPreset = async (id) => { await api.deleteVoicePreset(id); await loadPresets(); };
  const addWord = async () => {
    const w = wordInput.trim().toLowerCase();
    if (!w || local.banned_words.includes(w)) return;
    setWordInput(""); await commit({ banned_words: [...local.banned_words, w] });
  };
  const takeover = async (id) => { const u = await api.takeover(id); setConversations((c) => c.map((x) => x.id === id ? u : x)); toast.success("You're driving. KURT is silent on this thread."); };
  const handback = async (id) => { const u = await api.handback(id); setConversations((c) => c.map((x) => x.id === id ? u : x)); toast.success("Back to KURT with your context included."); };

  const addSupp = async () => {
    const e = suppInput.trim().toLowerCase();
    if (!e) return;
    setSuppInput("");
    setSuppression(await api.addSuppression(e));
    toast.success(`${e} suppressed — KURT will never contact them.`);
  };
  const removeSupp = async (e) => setSuppression(await api.removeSuppression(e));
  const addUnsub = async () => {
    const w = unsubInput.trim().toLowerCase();
    if (!w || (local.unsubscribe_words || []).includes(w)) return;
    setUnsubInput("");
    await commit({ unsubscribe_words: [...(local.unsubscribe_words || []), w] });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="control-panel" side="right" className="w-full sm:max-w-md overflow-y-auto bg-[#0B0F17] border-l border-white/10 text-slate-200 p-0">
        <SheetHeader className="sticky top-0 z-10 px-5 py-4 border-b border-white/10 bg-[#0B0F17]/95 backdrop-blur-xl">
          <SheetTitle className="kz-head text-slate-50 flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-400" /> Your control</SheetTitle>
          <SheetDescription className="sr-only">Set limits, brand boundaries, take over conversations, manage integrations, and pause everything.</SheetDescription>
        </SheetHeader>

        <div className="p-5 space-y-7">
          {/* Master pause */}
          <button data-testid="control-master-pause-btn" onClick={onTogglePause}
            className={`w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-semibold text-sm transition-all hover:scale-[1.01] ${paused ? "bg-emerald-500 text-[#04140d] hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-600 kz-pulse-danger"}`}>
            {paused ? <Play size={17} /> : <Power size={17} />} {paused ? "Resume everything" : "Pause everything"}
          </button>

          {/* Auto-send mode */}
          <div data-testid="auto-send-block">
            <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><Send size={16} className="text-emerald-400" /> Sending mode</h3>
            <p className="text-xs text-slate-500 mb-3">Choose how KURT handles copy that clears the compliance gate. Anything needing a nod still waits for you.</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="mode-draft-btn" onClick={() => commit({ auto_send: false })}
                className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl border text-sm font-semibold transition-all ${!local.auto_send ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-white/10 bg-[#121824] text-slate-400 hover:bg-white/[0.04]"}`}>
                <PenLine size={16} /> Draft for my approval
              </button>
              <button data-testid="mode-auto-btn" onClick={() => commit({ auto_send: true })}
                className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl border text-sm font-semibold transition-all ${local.auto_send ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-white/10 bg-[#121824] text-slate-400 hover:bg-white/[0.04]"}`}>
                <Send size={16} /> KURT sends automatically
              </button>
            </div>
            {local.auto_send && <p className="text-[11px] text-amber-400/90 mt-2">Auto-send is on. Compliance-cleared messages go out without waiting — cold/flagged ones still need your nod.</p>}
          </div>

          {/* House voice + presets */}
          <div data-testid="voice-block">
            <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><PenLine size={16} className="text-emerald-400" /> KURT's house voice</h3>
            <p className="text-xs text-slate-500 mb-3">Set your brand voice once — every auto-written message uses it. Save presets and switch per campaign.</p>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="voice-presets">
              {voicePresets.map((p) => {
                const active = (local.tone || "").trim() === (p.tone || "").trim();
                return (
                  <span key={p.id} data-testid={`preset-${p.id}`} className={`flex items-center gap-1.5 text-xs pl-3 pr-1.5 py-1.5 rounded-full border transition-all ${active ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 bg-white/[0.04] text-slate-300"}`}>
                    <button data-testid={`activate-preset-${p.id}`} onClick={() => activatePreset(p.id)} className="font-semibold">{p.name}</button>
                    <button data-testid={`del-preset-${p.id}`} onClick={() => delPreset(p.id)} className="w-4 h-4 rounded-full hover:bg-red-500/30 flex items-center justify-center"><X size={11} /></button>
                  </span>
                );
              })}
            </div>
            <textarea data-testid="voice-input" value={local.tone || ""} onChange={(e) => setLocal((l) => ({ ...l, tone: e.target.value }))}
              rows={2} placeholder="e.g. Confident, warm, plain-spoken. Short sentences. British. Never salesy."
              className="w-full bg-[#121824] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500/40 resize-y" />
            <div className="flex gap-2 mt-2">
              <button data-testid="save-voice-btn" onClick={() => commit({ tone: local.tone })}
                className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 transition-all"><Check size={13} /> Save voice</button>
              <button data-testid="save-preset-btn" onClick={savePreset}
                className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all"><Plus size={13} /> Save as preset</button>
            </div>
          </div>

          {/* Limits */}
          <div>
            <h3 className="kz-head font-bold mb-4">Limits</h3>
            <LimitSlider testid="slider-spend" label="Daily spend limit" value={local.daily_spend_limit} min={0} max={1000} step={25} prefix="£" onCommit={(v) => commit({ daily_spend_limit: v })} />
            <LimitSlider testid="slider-email" label="Daily email volume" value={local.daily_email_limit} min={0} max={500} step={10} suffix=" emails" onCommit={(v) => commit({ daily_email_limit: v })} />
            <LimitSlider testid="slider-discount" label="Max discount KURT can offer" value={local.max_discount} min={0} max={50} step={1} suffix="%" onCommit={(v) => commit({ max_discount: v })} />
          </div>

          {/* Brand */}
          <div>
            <h3 className="kz-head font-bold mb-1">Brand boundaries</h3>
            <p className="text-xs text-slate-500 mb-3">Words KURT will never use.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {local.banned_words.map((w) => (
                <span key={w} data-testid={`banned-${w}`} className="flex items-center gap-1.5 text-xs bg-white/[0.06] border border-white/10 pl-3 pr-1.5 py-1.5 rounded-full">
                  {w}<button data-testid={`remove-word-${w}`} onClick={() => commit({ banned_words: local.banned_words.filter((x) => x !== w) })} className="w-4 h-4 rounded-full hover:bg-red-500/30 flex items-center justify-center"><X size={11} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="banned-word-input" value={wordInput} onChange={(e) => setWordInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addWord()} placeholder="Add banned word…" className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
              <button data-testid="add-word-btn" onClick={addWord} className="w-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center"><Plus size={16} /></button>
            </div>
          </div>

          {/* Take over conversations */}
          <div>
            <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><MessageSquareText size={16} className="text-emerald-400" /> Conversations</h3>
            <p className="text-xs text-slate-500 mb-3">Step in mid-thread; hand back anytime.</p>
            <div className="space-y-2">
              {conversations.map((c) => {
                const mine = c.owner === "user";
                return (
                  <div key={c.id} data-testid={`conversation-${c.id}`} className="rounded-xl border border-white/[0.07] bg-[#121824] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5"><Flame size={12} className={c.heat === "hot" ? "text-red-400" : "text-amber-400"} /><span className="text-sm font-semibold text-slate-100 truncate">{c.prospect}</span></div>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{c.last}</p>
                      </div>
                      {mine ? (
                        <button data-testid={`handback-btn-${c.id}`} onClick={() => handback(c.id)} className="shrink-0 flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-emerald-500 text-[#04140d] hover:bg-emerald-400"><ArrowLeftRight size={12} /> Hand back</button>
                      ) : (
                        <button data-testid={`takeover-btn-${c.id}`} onClick={() => takeover(c.id)} className="shrink-0 flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-white/12 text-slate-200 hover:bg-white/[0.06]"><Hand size={12} /> Take over</button>
                      )}
                      <button data-testid={`del-convo-${c.id}`} onClick={async () => { await api.deleteConversation(c.id); setConversations((p) => p.filter((x) => x.id !== c.id)); }} className="shrink-0 text-slate-600 hover:text-red-400 transition-colors" title="Remove"><X size={13} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Integrations */}
          <div>
            <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><Plug size={16} className="text-emerald-400" /> KURT's hands</h3>
            <p className="text-xs text-slate-500 mb-3">Add a key and the power switches on.</p>
            <div className="space-y-1.5">
              {Object.entries(integrations).map(([name, info]) => (
                <div key={name} data-testid={`integration-${name}`} className="flex items-center gap-2 rounded-lg bg-[#121824] border border-white/[0.06] px-3 py-2">
                  {info.active ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> : <Circle size={14} className="text-slate-600 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <span className="text-sm capitalize text-slate-200">{name.replace("_", " ")}</span>
                    <p className="text-[10px] text-slate-500 truncate">{info.purpose}</p>
                  </div>
                  <span className={`text-[9px] font-bold uppercase ${info.active ? "text-emerald-400" : "text-slate-500"}`}>{info.active ? "Live" : info.oauth ? "OAuth" : "Add key"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Opt-out / suppression */}
          <div>
            <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><MailX size={16} className="text-emerald-400" /> Opt-outs & suppression</h3>
            <p className="text-xs text-slate-500 mb-3">Anyone here is off-limits forever. KURT also auto-suppresses anyone who replies with an unsubscribe word.</p>

            <div className="flex gap-2 mb-3">
              <input data-testid="suppression-input" value={suppInput} onChange={(e) => setSuppInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSupp()} placeholder="email@company.com to block…" className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
              <button data-testid="add-suppression-btn" onClick={addSupp} className="w-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center"><Plus size={16} /></button>
            </div>
            <div className="space-y-1.5 mb-4" data-testid="suppression-list">
              {suppression.length === 0 && <p className="text-xs text-slate-500">No one suppressed yet.</p>}
              {suppression.map((s) => (
                <div key={s.email} data-testid={`suppression-${s.email}`} className="flex items-center gap-2 rounded-lg bg-[#121824] border border-white/[0.06] px-3 py-2">
                  <Ban size={13} className="text-red-400 shrink-0" />
                  <span className="text-sm text-slate-200 truncate flex-1">{s.email}</span>
                  <span className="text-[10px] text-slate-500">{s.reason}</span>
                  <button data-testid={`remove-suppression-${s.email}`} onClick={() => removeSupp(s.email)} className="w-5 h-5 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={12} /></button>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wider">Unsubscribe words</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {(local.unsubscribe_words || []).map((w) => (
                <span key={w} data-testid={`unsub-${w}`} className="flex items-center gap-1.5 text-xs bg-white/[0.06] border border-white/10 pl-3 pr-1.5 py-1.5 rounded-full">
                  {w}<button data-testid={`remove-unsub-${w}`} onClick={() => commit({ unsubscribe_words: local.unsubscribe_words.filter((x) => x !== w) })} className="w-4 h-4 rounded-full hover:bg-red-500/30 flex items-center justify-center"><X size={11} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="unsub-input" value={unsubInput} onChange={(e) => setUnsubInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addUnsub()} placeholder="Add unsubscribe word…" className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
              <button data-testid="add-unsub-btn" onClick={addUnsub} className="w-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center"><Plus size={16} /></button>
            </div>
          </div>

          {/* Compliance audit log */}
          <div>
            <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><FileClock size={16} className="text-emerald-400" /> Compliance log</h3>
            <p className="text-xs text-slate-500 mb-3">Every check, block and send KURT made.</p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="audit-log">
              {audit.length === 0 && <p className="text-xs text-slate-500">No entries yet.</p>}
              {audit.map((e, i) => {
                const blocked = e.blocks && e.blocks.length > 0;
                const isSend = e.kind === "send";
                return (
                  <div key={i} data-testid={`audit-entry-${i}`} className="rounded-lg bg-[#121824] border border-white/[0.06] px-3 py-2">
                    <div className="flex items-center gap-2">
                      {isSend ? <Check size={13} className="text-emerald-400 shrink-0" /> : blocked ? <Ban size={13} className="text-red-400 shrink-0" /> : e.requires_approval ? <ShieldCheck size={13} className="text-amber-400 shrink-0" /> : <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />}
                      <span className="text-xs text-slate-200 truncate flex-1">{isSend ? `Sent → ${e.email}` : (e.action_title || "Compliance check")}</span>
                      <span className={`text-[9px] font-bold uppercase shrink-0 ${blocked ? "text-red-400" : e.requires_approval ? "text-amber-400" : "text-emerald-400"}`}>{isSend ? "sent" : blocked ? "blocked" : e.requires_approval ? "needs nod" : "passed"}</span>
                    </div>
                    {(e.blocks || []).concat(e.reasons || []).slice(0, 2).map((r, j) => (
                      <p key={j} className="text-[10px] text-slate-500 mt-1 pl-5 leading-snug">{r}</p>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <TemplatesLibrary />

          <ReferralScheme />

          <CompliancePolicy />

          <p className="text-[11px] text-slate-600 leading-relaxed pt-2 border-t border-white/[0.06]">
            KURT can never fabricate contacts, contact anyone after opt-out, run prohibited automation, or deceive. Compliance overrides revenue — enforced in code.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const LimitSlider = ({ label, value, min, max, step, prefix = "", suffix = "", onCommit, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="mb-5 last:mb-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-300">{label}</span>
        <span className="kz-mono text-sm font-semibold text-emerald-400">{prefix}{v}{suffix}</span>
      </div>
      <Slider value={[v]} min={min} max={max} step={step} onValueChange={(x) => setV(x[0])} onValueCommit={(x) => onCommit(x[0])} className="cursor-pointer" />
    </div>
  );
};
